// Shared wiring for the premium WebGL foil (foilShader.js). Three call sites
// build a near-identical uniform set + load the same matcap / sibling baked
// foil map: the single-card inspector (FoilCanvas.jsx), the deck-grid stage
// (foilStage.js), and the board (cardMesh.js). This module owns that wiring so
// the uniforms, placeholder, matcap, and foil-map URL derivation live in ONE
// place.
//
// The ONLY intentional divergence between the call sites is the card texture's
// colorSpace: FoilCanvas + foilStage own a NoColorSpace / LinearSRGB-output
// renderer (raw passthrough), while the board shares the main sRGB-output scene
// renderer and so keeps SRGBColorSpace on uTex. That difference is now a single
// explicit `texColorSpace` argument to loadFoilTextures(). The matcap and baked
// foil map are DATA (not colour), so they are always NoColorSpace everywhere.

import * as THREE from 'three';
import { CARD_ASPECT } from '../../lib/cardCorner.js';
import { FOIL_WHITE_ZONES } from './foilZones.js';
import { DEFAULT_FOIL_PARAMS, paramUniformName } from './foilParams.js';
import { isDevBuild } from '../devMode.js';
import { corsTextureUrl } from '../cardImageUrl.js';

const MATCAP_URL = '/foil/studio-matcap.webp';

// 1x1 neutral-grey placeholder so the matcap/foilmap samplers are never null
// before the real textures decode (a null sampler2D renders black). One shared
// instance app-wide. It is read-only and never disposed.
let _placeholder = null;
export function foilPlaceholderTexture() {
  if (_placeholder) return _placeholder;
  const t = new THREE.DataTexture(new Uint8Array([40, 40, 44, 255]), 1, 1, THREE.RGBAFormat);
  t.colorSpace = THREE.NoColorSpace;
  t.needsUpdate = true;
  _placeholder = t;
  return _placeholder;
}

// Studio-lighting matcap for the real environment reflection (replaces the
// procedural studioEnv when uMatcapOn > 0.5). Loaded ONCE, shared across every
// caller. Returns the current texture (the grey placeholder until the real one
// decodes). Pass `onReady(tex)` to be called when the real matcap decodes so a
// caller can re-point its uMatcap uniform (uniforms built before the load).
let _matcap = null;
const _matcapReadyCallbacks = new Set();
export function getFoilMatcap(onReady) {
  if (_matcap) {
    if (onReady) onReady(_matcap);
    return _matcap;
  }
  if (onReady) _matcapReadyCallbacks.add(onReady);
  if (!_matcapLoading) {
    _matcapLoading = true;
    new THREE.TextureLoader().load(MATCAP_URL, (tex) => {
      tex.colorSpace = THREE.NoColorSpace;
      _matcap = tex;
      for (const cb of _matcapReadyCallbacks) cb(tex);
      _matcapReadyCallbacks.clear();
    });
  }
  return foilPlaceholderTexture();
}
let _matcapLoading = false;

// Derive the sibling baked foil-map URL from a card art URL: strip any query,
// swap `.webp` -> `.foilmap4.webp`, and (dev only) append a cache-bust so a
// locally re-baked map shows on reload despite the immutable cache header. Prod
// keeps the bare URL so it caches normally. Single source for this derivation.
export function foilMapUrl(imageUrl) {
  const bust = isDevBuild() ? '?t=' + Date.now() : '';
  return imageUrl.split('?')[0].replace(/\.webp$/i, '.foilmap4.webp') + bust;
}

// Sibling baked monocular depth map (Depth-Anything V2), drives the subject-
// float parallax. Same derivation as foilMapUrl: strip query, swap suffix, dev
// cache-bust so a locally re-baked depth map shows on reload.
export function depthMapUrl(imageUrl) {
  const bust = isDevBuild() ? '?t=' + Date.now() : '';
  return imageUrl.split('?')[0].replace(/\.webp$/i, '.depthmap.webp') + bust;
}

// Sibling URL for the baked STATIC rest-frame foil composite (the foil shader's
// straight-on output baked to a plain image). Resting foil cards display this as
// a normal <img> (zero WebGL), only swapping to a live FoilCanvas on hover.
// Unlike foilMapUrl this PRESERVES the source's ?cb=<appVersion> query (instead
// of a Date.now() bust) so the grid <img> caches normally per release. A
// per-mount cache-bust would re-download on every scroll/virtualization mount.
export function foilRestUrl(imageUrl) {
  const [base, q] = imageUrl.split('?');
  const rest = base.replace(/\.webp$/i, '.foilrest.webp');
  return q ? rest + '?' + q : rest;
}

// Build a fresh uniforms object for the foil ShaderMaterial. uTex starts null
// (callers load + assign their own card texture via loadFoilTextures, applying
// the appropriate colorSpace). uMatcap points at the shared matcap (placeholder
// until it decodes). zones default to the spell name-plate/text-box rects; sites
// and avatars pass their own frame zones (see foilZonesForCard). cornerRadius defaults to 0 (DOM clips the
// quad); the shared stage passes a positive radius to round its own quad.
export function buildFoilUniforms({ zones = FOIL_WHITE_ZONES, cornerRadius = 0, quadAspect = 1 / CARD_ASPECT } = {}) {
  const z0 = zones[0];
  const z1 = zones[1] || z0;
  const placeholder = foilPlaceholderTexture();
  const uniforms = {
    uTex: { value: null },
    uMatcap: { value: getFoilMatcap() },
    uFoilMap: { value: placeholder },
    uHasFoilMap: { value: 0 },
    uDepthMap: { value: placeholder },
    uHasDepthMap: { value: 0 },
    uTexel: { value: new THREE.Vector2(1 / 744, 1 / 1039) },
    uTilt: { value: new THREE.Vector2(0, 0) },
    uFromCenter: { value: 0 },
    uTime: { value: 0 },
    uZone0: { value: new THREE.Vector4(z0.x, z0.y, z0.w, z0.h) },
    uZone1: { value: new THREE.Vector4(z1.x, z1.y, z1.w, z1.h) },
    uCornerRadius: { value: cornerRadius },
    uQuadAspect: { value: quadAspect },
    // Whole-quad fade, applied on top of the shape alpha. Opaque by default.
    // only callers that cross-fade an individual card drive it.
    uOpacity: { value: 1 },
    // Use the card art's own alpha as output alpha (rounded corners on a flat
    // sharp quad, e.g. the cemetery pile top face). Default 0 = opaque.
    uUseTexAlpha: { value: 0 },
    // Object-cover sampling crop (CHANGE 1). Default identity (uvc == vUv); the
    // stage sets these per-entry from the quad-vs-texture aspect so landscape
    // site art center-crops into the portrait tile like the DOM object-cover
    // <img>. FoilCanvas leaves them at default (portrait art matches its quad).
    uUvScale: { value: new THREE.Vector2(1, 1) },
    uUvOffset: { value: new THREE.Vector2(0, 0) },
    // Site 90deg CW rotation (single-quad surfaces only; grid rotates its mesh).
    uRotate90: { value: 0 },
    // Scene-lit branch (board only). DOM callers (FoilCanvas, foilStage) leave
    // uSceneLit at 0, so the shader runs the existing screen-space self-lit
    // model and the other three uniforms are ignored. cardMesh sets uSceneLit=1
    // and feeds uLightDir/uLightColor/uAmbient per frame from the scene lights.
    // uLightColor/uAmbient are linear rgb * intensity; default to a neutral
    // unlit pass-through (white light, no ambient) so a board foil never goes
    // black before the first updateFoilSheens tick wires the real values.
    uSceneLit: { value: 0 },
    uLightDir: { value: new THREE.Vector3(0, 0, 1) },
    uLightColor: { value: new THREE.Vector3(1, 1, 1) },
    uAmbient: { value: new THREE.Vector3(0, 0, 0) },
    // Board parity: sRGB-decode the output to cancel the scene renderer's sRGB
    // encode. DOM callers (LinearSRGB output) leave this 0.
    uOutputLinear: { value: 0 },
  };
  for (const k of Object.keys(DEFAULT_FOIL_PARAMS)) {
    uniforms[paramUniformName(k)] = { value: DEFAULT_FOIL_PARAMS[k] };
  }
  return uniforms;
}

// Load the foil's two textures and hand them back via callbacks. The card art
// is loaded with the given `texColorSpace` (NoColorSpace for the raw-passthrough
// FoilCanvas/foilStage renderers, SRGBColorSpace for the board's sRGB-output
// scene renderer). The baked foil map is always NoColorSpace (it's data). If the
// foil map 404s (not yet downloaded) the error callback is a no-op so the shader
// keeps its runtime-derived fallback. `loader` lets callers reuse a shared
// TextureLoader (e.g. the board's crossOrigin loader + GPU-upload onLoad).
// Downscale a just-loaded texture's image in place when it carries more
// texels than the caller's render size can resolve (`maxSize` = cap on the
// image's larger edge, in px). Grid tiles render foil cards at a few hundred
// px, while the foilmap ships at 744×1040. decoding past the render size
// only inflates GPU + decoded-image memory. The small canvas replaces the
// image as the upload source, so the full-size decode becomes collectable.
function capTextureSize(tex, maxSize) {
  const img = tex.image;
  if (!img || !maxSize) return;
  const long = Math.max(img.width || 0, img.height || 0);
  if (long <= maxSize) return;
  const s = maxSize / long;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(img.width * s));
  c.height = Math.max(1, Math.round(img.height * s));
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
  tex.image = c;
}

export function loadFoilTextures(imageUrl, { texColorSpace, onTex, onFoilMap, onDepthMap, loader, maxSize } = {}) {
  const ld = loader || new THREE.TextureLoader();
  if (onTex) {
    ld.load(corsTextureUrl(imageUrl), (tex) => {
      if (maxSize) capTextureSize(tex, maxSize);
      tex.colorSpace = texColorSpace;
      // Anisotropic + mipmapped sampling so the 744px art stays crisp when
      // minified into a small card quad (hand/grid) or viewed at an oblique
      // tilt. Without it, low-DPR (Windows) renders alias/blur. WebGL2 (Three
      // r183) supports mipmaps on these NPOT textures.
      tex.anisotropy = 8;
      tex.generateMipmaps = true;
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.needsUpdate = true;
      onTex(tex);
    });
  }
  if (onFoilMap) {
    ld.load(
      corsTextureUrl(foilMapUrl(imageUrl)),
      (tex) => {
        if (maxSize) capTextureSize(tex, maxSize);
        tex.colorSpace = THREE.NoColorSpace;
        onFoilMap(tex);
      },
      undefined,
      () => { /* not on disk yet -> keep runtime-derived fallback */ },
    );
  }
  if (onDepthMap) {
    ld.load(
      corsTextureUrl(depthMapUrl(imageUrl)),
      (tex) => {
        if (maxSize) capTextureSize(tex, maxSize);
        tex.colorSpace = THREE.NoColorSpace;
        onDepthMap(tex);
      },
      undefined,
      () => { /* not on disk yet -> keep centre-radial estimate */ },
    );
  }
}
